import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef, OnChanges } from '@angular/core';
import { combineLatest, filter, Subscription } from 'rxjs';
import { TabComponent } from '@Common/Layout/components/Tab/tab.component';
import { UserNoteModel } from '@Common/Misc/models/user-note.model';
import { NotesChange } from '../UserNotes/user-notes.component';
import { UserModel } from '@Core/Users/models/user.model';
import { CurrentUserStateService } from '@Core/Users/services/current-user-state.service';
import { TargetingRhpContextsService } from '@Common/TargetingShared/services/targeting-rhp-contexts.service';
import { PrivateDataModel } from '@Discover/Targeting/models/private-data.model';
import { ContactService } from '@Common/TargetingShared/services/contact-service';
import { ContactModel } from '@Common/TargetingShared/model/contact.model';
import { OutletModel } from '@Discover/Targeting/models/outlet.model';
import { MessageService } from 'primeng/api';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { EntityType } from '@api/targeting/enum/entity.type.enum';

enum AccountTypesTabIds {
    CONTACT = 'CONTACT',
    OUTLET = 'OUTLET',
}

@Component({
    selector: 'agpr-targeting-notes',
    templateUrl: './notes.component.html',
    styleUrls: ['./notes.component.scss'],
    standalone: false
})
export class NotesTargetingComponent implements OnInit, OnDestroy, OnChanges {
    @Input() _entityType: EntityType;
    @Input() disableOutletAddNotes: boolean;
    @Output() notesLengthNumber = new EventEmitter<{contactsLength: number; outletLength: number}>();
    subscriptions: Subscription[] = [];
    readonly entityTypes = EntityType;


    selectedTabId: string = AccountTypesTabIds.CONTACT;
    selectedTabIndex: number = 0;
    tabIds = AccountTypesTabIds;
    contactEntity: ContactModel;
    outletEntity: OutletModel;
    contactUserNotes: UserNoteModel[] = [];
    outletUserNotes: UserNoteModel[] = [];
    currentUser: UserModel;
    contactPrivateData: PrivateDataModel;
    outletPrivateData: PrivateDataModel;

    constructor(private currentUserService: CurrentUserStateService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public contactService: ContactService,
        public messageService: MessageService,
        public localizationService: LocalizationService,
        private cd: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.subscriptions.push(
            this.currentUserService.getUser().subscribe(user => {
                this.currentUser = user;
            }),
            combineLatest([
                this.targetingRhpContextsService.getContact(),
                this.targetingRhpContextsService.getContactPrivateData(),
                this.targetingRhpContextsService.getOutletPrivateData(),
            ]).subscribe(([contact, contactPrivateData, outletPrivateData]) => {
                this.contactEntity = contact;
                this.contactUserNotes = [];
                this.contactPrivateData = contactPrivateData;

                if (this.contactEntity?.privateContactMetadata) {
                    this.contactUserNotes = this.contactEntity.userNotes;
                } else if (contactPrivateData) {
                    this.contactUserNotes = contactPrivateData.notes;
                }

                this.outletPrivateData = outletPrivateData;
                this.outletUserNotes = outletPrivateData ? outletPrivateData?.notes : [];

                this.notesLengthNumber.emit({ contactsLength: this.contactUserNotes?.length, outletLength: this.outletUserNotes?.length });
            }),
            this.contactService.getNotesAllLayerUpdate().subscribe(update => {
                if (update.entityType === this.entityTypes.CONTACT && update.entityId === this.contactEntity?.id) {
                    this.contactUserNotes = update.notes;
                    this.notesLengthNumber.emit({ contactsLength: update.notes.length, outletLength: this.outletUserNotes?.length });
                } else if (update.entityType === this.entityTypes.OUTLET && update.entityId === this.outletEntity?.id) {
                    this.outletUserNotes = update.notes;
                    this.notesLengthNumber.emit({ contactsLength: this.contactUserNotes?.length, outletLength: update.notes.length });
                }
            }),
            this.targetingRhpContextsService.getOutlet().subscribe(x => {
                this.outletEntity = x.outlet;
                if (!this.outletEntity?.id) {
                    this.outletUserNotes = [];
                    this.notesLengthNumber.emit({ contactsLength: this.contactUserNotes?.length, outletLength: 0 });
                    if (this._entityType !== this.entityTypes.OUTLET) {
                        this.selectedTabIndex = 0;
                    }
                }
            }),

        );

    }

    ngOnChanges() {
        if (this._entityType !== this.entityTypes.CONTACT) {
            this.selectedTabIndex = 1;
            this.selectedTabId = AccountTypesTabIds.OUTLET;
        } else {
            this.selectedTabIndex = 0;
            this.selectedTabId = AccountTypesTabIds.CONTACT;
        }
        this.cd.detectChanges();
    }

    tabChange(tab: TabComponent): void {
        this.selectedTabId = tab?.id;
    }

    tabIndexChange(index: number): void {
        this.selectedTabIndex = index;
    }

    modifyNotes(notesChange: NotesChange) {
        let entity;
        let privateData;
        if (this.selectedTabId === AccountTypesTabIds.CONTACT) {
            entity = this.contactEntity;
            privateData = this.contactPrivateData;
        } else {
            entity = this.outletEntity;
            privateData = this.outletPrivateData;
        }
        this.targetingRhpContextsService.modifyNotes(notesChange?.notes, entity, privateData)
            .then(done => {
                if (this.selectedTabId === this.tabIds.CONTACT) {
                    this.contactUserNotes = notesChange?.notes;
                } else {
                    this.outletUserNotes = notesChange?.notes;
                }

                this.messageService.add({
                    severity: messageSeverityEnum.success,
                    summary: this.localizationService.transform(`Discover.userNotes.${notesChange.type}SuccessMessage`)
                });
                this.contactService.setNotesAllLayerUpdate(
                    { entityId: entity?.id, entityType: entity?.entityType, notes: notesChange?.notes }
                );
            })
            .catch(error => {
                if (this.selectedTabId === this.tabIds.CONTACT) {
                    this.contactUserNotes = this.contactUserNotes.slice();
                } else {
                    this.outletUserNotes = this.outletUserNotes.slice();
                }
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: this.localizationService.transform(`Discover.userNotes.${notesChange.type}ErrorMessage`)
                });
            });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
